import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import apiClient from "../../../../services/api";

interface DiscoveryConfig {
  articleListSelector?: string;
  articleContentSelector?: string;
  urlPattern?: string;
  endpoint?: string;
  extractAsStaticPage?: boolean;
  contentExcludeSelectors?: string[];
}

interface CreateSourcePayload {
  name: string;
  sourceType: string;
  url: string;
  projectId: string | null;
  checkFrequency: string;
  isActive: boolean;
  discoveryConfig: DiscoveryConfig | null;
}

interface ApiErrorDetail {
  field: string;
  message: string;
}

interface ApiErrorResponse {
  message?: string;
  details?: ApiErrorDetail[];
}

interface UseSourceMutationsOptions {
  sourceId?: string | null;
  onSuccess: () => void;
  onFieldErrors: (errors: Record<string, string>) => void;
  onGlobalError: (message: string) => void;
}

export function useSourceMutations({
  sourceId,
  onSuccess,
  onFieldErrors,
  onGlobalError,
}: UseSourceMutationsOptions) {
  const queryClient = useQueryClient();

  const handleError = (error: AxiosError<ApiErrorResponse>) => {
    if (error.response?.status && error.response.status >= 500) {
      onGlobalError("Błąd serwera. Spróbuj ponownie później.");
    } else if (!error.response) {
      onGlobalError("Błąd połączenia. Sprawdź połączenie z internetem.");
    } else if (error.response?.data?.details) {
      const newErrors: Record<string, string> = {};
      error.response.data.details.forEach((detail) => {
        newErrors[detail.field] = detail.message;
      });
      onFieldErrors(newErrors);
    } else {
      onGlobalError("Wystąpił nieoczekiwany błąd. Spróbuj ponownie.");
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: CreateSourcePayload) => {
      await apiClient.post("/grants/sources", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
      onSuccess();
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CreateSourcePayload) => {
      await apiClient.put(`/grants/sources/${sourceId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
      queryClient.invalidateQueries({ queryKey: ["grants-source", sourceId] });
      onSuccess();
    },
    onError: handleError,
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const submit = (payload: CreateSourcePayload) => {
    if (sourceId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return {
    submit,
    isLoading,
    createMutation,
    updateMutation,
  };
}

export type { CreateSourcePayload, DiscoveryConfig };
